import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { courierProfileApi } from '../api/courier-profile-api';
import {
  courierProfileKeys,
  courierProfileQueries,
} from '../api/courier-profile-queries';
import type { CourierProfileId } from './schema';
import type { CourierOrdersSearch } from './orders';

/** Courier profile header (summary card + Profile-tab personal fields). */
export function useCourierProfile(id: CourierProfileId) {
  return useQuery(courierProfileQueries.detail(id));
}

/** The courier's order history (Orders tab table). */
export function useCourierOrders(id: CourierProfileId) {
  return useQuery(courierProfileQueries.orders(id));
}

export function useCourierOrderList(id: CourierProfileId, filter: CourierOrdersSearch) {
  return useQuery(courierProfileQueries.orderList(id, filter));
}

/** Contract form values + requirement switches (Contract tab). */
export function useCourierContract(id: CourierProfileId) {
  return useQuery(courierProfileQueries.contract(id));
}

/** Issued equipment (Equipment tab grid). */
export function useCourierEquipment(id: CourierProfileId) {
  return useQuery(courierProfileQueries.equipment(id));
}

/** Live courier GPS location from the same cache endpoint as legacy cPanel. */
export function useCourierProfileLocation(id: CourierProfileId) {
  return useQuery(courierProfileQueries.location(id));
}

/** Battery telemetry and GPS from its status stream. */
export function useCourierBatteryStatus(
  batteryId: CourierProfileId | null | undefined,
  courierId: CourierProfileId,
) {
  return useQuery(courierProfileQueries.batteryLocation(batteryId ?? '', courierId));
}

const PICKER_PAGE_SIZE = 15;

/** Searchable, infinite scooter picker (scooters not yet attached to a courier). */
export function useCourierScooterOptions(search: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: courierProfileKeys.scooterOptions(search),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      courierProfileApi.listScooters({
        search,
        skip: pageParam,
        limit: PICKER_PAGE_SIZE,
      }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count && last.data.length > 0 ? loaded : undefined;
    },
    enabled,
  });
}

/** Searchable, infinite battery picker (by device id). */
export function useCourierBatteryOptions(search: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: courierProfileKeys.batteryOptions(search),
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      courierProfileApi.listBatteries({
        search,
        skip: pageParam,
        limit: PICKER_PAGE_SIZE,
      }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count && last.data.length > 0 ? loaded : undefined;
    },
    enabled,
  });
}
